import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { addDays, startOfDay } from 'date-fns';
import { normalize, schema } from 'normalizr';

import { combineReducers } from 'redux';
import { createTableSlice, Filters } from '../../../common/datagrid';
import {
    PriceChangeDirection,
    PricePeriodDto,
    PricePlanDto,
    TableType
} from '../../../common/PricePrgClient';
import { createAsyncAction, toDateString } from '../../../utils';
import { fetchUserSettings } from '../../settings/redux/settingsSlices';
import { fetchWarehouses } from '../../stock/redux/stockSlices';
import { dayAmount, PricePlan, UpdatePricePlan } from '../basePriceTypes';

const namespaces = {
    pricePlans: 'pricePlans',
    showAllArticles: 'showAllArticles',
    showActivePricelist: 'showActivePricelist',
    selectedPricelistId: 'selectedPricelistId',
    selectedWarehouseDate: 'selectedWarehouseDate',
    isBasePricesLoading: 'isBasePricesLoading',
    isExporting: 'isExporting'
};

export const updateDefaultBasePricelistRequest = createAction<number>(
    '@userSettings/updateDefaultBasePricelistRequest'
);
export const updateDefaultBasePricelistSuccess = createAction(
    '@userSettings/updateDefaultBasePricelistSuccess'
);

const mapPricePlanDtoToPricePlan = (bpPlan: PricePlanDto) =>
    ({
        productId: bpPlan.productId,
        pricePeriods: bpPlan.pricePeriods.sort((a, b) => a.dayIndexFrom - b.dayIndexFrom)
    } as PricePlan);

const pricePlanEntity = new schema.Entity<PricePlan>('pricePlans', undefined, {
    idAttribute: 'productId'
});

type FetchPricePlansRequest = { pricelistId: number; dayAmount: number };
type PricePlanType = { [productId: string]: PricePlan };
type UpdatePricePlanRequest = UpdatePricePlan & {
    isAwareOfChangingActualPricelist?: boolean;
    isAwareOfCreatingNewPricelistPeriods?: boolean;
};
type UpdatePricePlanSuccess = { updated: PricePlanDto[]; deleted: number[] };

export const fetchPricePlans = createAsyncAction<FetchPricePlansRequest, PricePlanDto[]>(
    namespaces.pricePlans,
    'fetchPricePlans'
);
export const updatePricePlan = createAsyncAction<UpdatePricePlanRequest, UpdatePricePlanSuccess>(
    namespaces.pricePlans,
    'updatePricePlan'
);
const pricePlansSlice = createSlice({
    name: namespaces.pricePlans,
    initialState: {} as PricePlanType,
    reducers: {
        cancelPricePlanUpdate: (state, action: PayloadAction<{ productId: number; dayIndex: number }>) => {
            const { payload } = action;
            const pricePlan = state[payload.productId];

            if (pricePlan == null) {
                return;
            }

            const { pricePeriods } = pricePlan;
            const period = pricePeriods.find(bpp => bpp.dayIndexFrom === payload.dayIndex);

            if (period != null) {
                if (period.priceId === 0) {
                    if (pricePeriods.length === 1) {
                        delete state[payload.productId];
                    } else {
                        pricePlan.pricePeriods.splice(pricePlan.pricePeriods.indexOf(period), 1);

                        for (const laterPp of pricePeriods.filter(
                            bpp => bpp.dayIndexFrom > payload.dayIndex
                        )) {
                            delete laterPp.priceToBeSaved;
                        }
                        // Revert latest price period to be valid further since we cut it before
                        const prevPricePeriod = pricePlan.pricePeriods.slice(-1)[0];
                        if (prevPricePeriod != null) {
                            prevPricePeriod.dayIndexTo = dayAmount;
                        }
                    }
                } else {
                    for (const pp of pricePeriods.filter(bpp => bpp.dayIndexFrom >= payload.dayIndex)) {
                        delete pp.priceToBeSaved;
                    }
                }
            }
        }
    },
    extraReducers: builder =>
        builder
            .addCase(fetchPricePlans.success, (_, action) => {
                const sorted = action.payload.map(mapPricePlanDtoToPricePlan);
                const normalized = normalize(sorted, [pricePlanEntity]);

                return normalized.entities.pricePlans ?? {};
            })
            .addCase(updatePricePlan.request, (state, action) => {
                const { payload } = action;

                const getPreviousPrice = (pp: PricePlanDto) => {
                    const previousPeriod = pp.pricePeriods.reduce((previousPrice, current) => {
                        if (current.price != null && current.dayIndexFrom < payload.dayIndex) {
                            if (previousPrice == null) {
                                return current;
                            }

                            if (previousPrice.dayIndexFrom < current.dayIndexFrom) {
                                return current;
                            }
                        }
                        return previousPrice;
                    }, null as PricePeriodDto | null);

                    return previousPeriod;
                };

                const pricePlan = state[payload.productId];

                if (pricePlan == null) {
                    state[payload.productId] = {
                        productId: payload.productId,
                        pricePeriods: [
                            {
                                dayIndexFrom: payload.dayIndex,
                                dayIndexTo: payload.dayIndex + dayAmount,
                                price: 0,
                                priceToBeSaved: payload.newPrice,
                                priceChangeIndicator: PriceChangeDirection.NewArticle,
                                priceId: 0
                            }
                        ]
                    };
                    return;
                }

                const { pricePeriods } = pricePlan;
                const period = pricePeriods.find(bpp => bpp.dayIndexFrom === payload.dayIndex);
                const prevPrice = getPreviousPrice(pricePlan);

                if (period != null) {
                    period.priceToBeSaved = payload.newPrice;
                } else {
                    let idxToInsertAt = 0;
                    let dayIndexTo = payload.dayIndex + dayAmount;
                    for (let i = 0; i < pricePeriods.length; i++) {
                        if (pricePeriods[i].dayIndexFrom < payload.dayIndex) {
                            idxToInsertAt = i;
                        } else {
                            dayIndexTo = pricePeriods[i].dayIndexFrom - 1;
                            break;
                        }
                    }

                    pricePeriods.splice(idxToInsertAt, 0, {
                        dayIndexFrom: payload.dayIndex,
                        dayIndexTo: dayIndexTo,
                        price: 0,
                        priceToBeSaved: payload.newPrice,
                        priceChangeIndicator: PriceChangeDirection.Unaffected,
                        priceId: 0
                    });
                }

                for (const pp of pricePeriods) {
                    if (pp.dayIndexFrom > payload.dayIndex) {
                        pp.priceToBeSaved = payload.newPrice;
                    }
                }

                if (prevPrice != null) {
                    prevPrice.dayIndexTo = Math.max(prevPrice.dayIndexFrom, payload.dayIndex - 1);
                }
            })
            .addCase(updatePricePlan.success, (state, action) => {
                const { payload } = action;

                for (const updatedProduct of payload.updated) {
                    state[updatedProduct.productId] = updatedProduct;
                }

                for (const deletedProductId of payload.deleted) {
                    delete state[deletedProductId];
                }
            })
});

export const { cancelPricePlanUpdate } = pricePlansSlice.actions;

const dayIdxFilters: Filters = {};
for (const dayIndex of Array.from(Array(dayAmount).keys())) {
    dayIdxFilters[dayIndex] = '';
}

const basePriceTableSlice = createTableSlice('basePriceTable', {
    table: TableType.PricePlan,
    filters: {
        productId: '',
        productName: '',
        customProductAttribute1: '',
        basePrice: '',
        purchaser: '',
        stock: '',
        grossWeight: '',
        netWeight: '',
        boxesPerPallet: '0',
        ...dayIdxFilters
    },
    showTooltips: true,
    sorting: {
        column: 'productId',
        direction: 'asc'
    },
    columns: [
        ...[
            'productId',
            'productName',
            'customProductAttribute1',
            'country',
            'priceUnit',
            'purchaser',
            'stock',
            'grossWeight',
            'netWeight',
            'boxesPerPallet'
        ],
        ...Array.from(Array(dayAmount).keys()).map(x => String(x))
    ].map(key => ({ key: key, hide: key === 'customProductAttribute1', width: undefined }))
});
export const { basePriceTableActions } = basePriceTableSlice;

const showAllArticlesSlice = createSlice({
    name: namespaces.showAllArticles,
    initialState: true,
    reducers: {
        showAllArticlesChanged: (state, action: PayloadAction<boolean>) => action.payload
    }
});

export const { showAllArticlesChanged } = showAllArticlesSlice.actions;

const showActivePricelistSlice = createSlice({
    name: namespaces.showActivePricelist,
    initialState: false,
    reducers: {
        showActivePricelistChanged: (state, action: PayloadAction<boolean>) => action.payload
    }
});
export const { showActivePricelistChanged } = showActivePricelistSlice.actions;

const selectedPricelistIdSlice = createSlice({
    name: namespaces.selectedPricelistId,
    initialState: null as number | null,
    reducers: {
        pricelistSelected: (state, action: PayloadAction<number>) => action.payload
    },
    extraReducers: builder =>
        builder.addCase(fetchUserSettings.success, (state, { payload }) => {
            if (payload?.selectedBasePricelistId != null && payload.selectedBasePricelistId > 0) {
                return payload.selectedBasePricelistId;
            }
            return state;
        })
});

export const { pricelistSelected } = selectedPricelistIdSlice.actions;

type SelectedWarehouseDate = {
    date: string;
    warehouse: number | null;
};

const selectedWarehouseDate = createSlice({
    name: namespaces.selectedWarehouseDate,
    initialState: {
        date: toDateString(addDays(startOfDay(new Date()), 1)),
        warehouse: null
    } as SelectedWarehouseDate,
    reducers: {
        warehouseDateSelected: (state, action: PayloadAction<{ date: string; warehouse: number }>) => {
            const { payload } = action;
            state.date = toDateString(payload.date);
            state.warehouse = payload.warehouse;
        }
    },
    extraReducers: builder =>
        builder.addCase(fetchWarehouses.success, (state, { payload }) => {
            if (payload.length === 0) {
                return;
            }

            state.warehouse =
                payload.find(w => w.isPrimaryWarehouse)?.warehouseId ?? payload[0].warehouseId ?? null;
        })
});
export const { warehouseDateSelected } = selectedWarehouseDate.actions;

const isLoadingSlice = createSlice({
    name: namespaces.isBasePricesLoading,
    initialState: false,
    reducers: {},
    extraReducers: builder =>
        builder.addCase(fetchPricePlans.request, () => true).addCase(fetchPricePlans.success, () => false)
});
const isExportingSlice = createSlice({
    name: namespaces.isExporting,
    initialState: false,
    reducers: {
        setIsExporting: (state, action: PayloadAction<boolean>) => action.payload
    }
});

export const { setIsExporting } = isExportingSlice.actions;
export default combineReducers({
    pricePlans: pricePlansSlice.reducer,
    selectedWarehouseDate: selectedWarehouseDate.reducer,
    ui: combineReducers({
        basePriceTable: basePriceTableSlice.reducer,
        showAllArticles: showAllArticlesSlice.reducer,
        selectedPricelistId: selectedPricelistIdSlice.reducer,
        showActivePricelist: showActivePricelistSlice.reducer,
        isLoading: isLoadingSlice.reducer,
        isExporting: isExportingSlice.reducer
    })
});
